import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { join } from '@nullvoxpopuli/ember-composable-helpers';
import { gt } from 'ember-truth-helpers';
import FileInput from 'garaje/components/file-input';
import UiCard from 'garaje/components/ui/card';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import MessageBox from 'garaje/components/ui/classic/message-box';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';

const MAX_CSV_SIZE_BYTES = 200 * 1024;

interface CsvUploadModalSignature {
  Args: {
    onUpload: (file: File) => void;
    onClose: () => void;
    isUploading: boolean;
    currentMappingCount: number;
    higherPrioritySources?: string[];
  };
}

export default class MacAddressMappingsCsvUploadModal extends Component<CsvUploadModalSignature> {
  @tracked selectedFile: File | null = null;
  @tracked fileError = '';
  @tracked csvRowCount: number | null = null;

  @action
  onFileSelect(file: File): void {
    this.fileError = '';
    this.csvRowCount = null;

    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      this.fileError = 'Please select a CSV file.';
      return;
    }

    if (file.size > MAX_CSV_SIZE_BYTES) {
      this.fileError = 'File is too large. Please select one under 200KB.';
      return;
    }

    this.selectedFile = file;
    void this.countCsvRows(file);
  }

  async countCsvRows(file: File): Promise<void> {
    try {
      const text = await file.text();
      const lines = text.split('\n').filter((line) => line.trim().length > 0);
      // Subtract 1 for the header row
      this.csvRowCount = Math.max(0, lines.length - 1);
    } catch {
      // If we can't read the file, just don't show the count
      this.csvRowCount = null;
    }
  }

  @action
  clearFile(): void {
    this.selectedFile = null;
    this.fileError = '';
    this.csvRowCount = null;
  }

  @action
  handleUpload(): void {
    if (!this.selectedFile) return;
    this.args.onUpload(this.selectedFile);
  }

  <template>
    <ModalDialog
      @targetAttachment="center"
      @translucentOverlay={{true}}
      @containerClass="ember-modal-dialog--header-modal !max-w-[600px]"
    >
      <div class="p-6" data-test-csv-upload-modal>
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-xl font-semibold">Upload CSV</h2>
          <button
            type="button"
            class="text-carbon-50 hover:text-carbon-70 text-xl leading-none"
            data-test-csv-upload-close
            {{on "click" @onClose}}
          >
            &times;
          </button>
        </div>

        <div class="mt-4">
          {{#if this.selectedFile}}
            <UiCard class="!pt-8 border-dashed border-carbon-40 text-center text-sm" data-test-csv-drop-zone>
              <span data-test-current-file>{{this.selectedFile.name}}</span>
              {{#if this.csvRowCount}}
                <span class="text-carbon-50" data-test-csv-row-count>
                  ({{this.csvRowCount}}
                  {{if (gt this.csvRowCount 1) "rows" "row"}})
                </span>
              {{/if}}
              <br />
              {{#unless @isUploading}}
                <button
                  type="button"
                  class="btn btn--link !text-gem-50 btn--small"
                  data-test-csv-remove-file
                  {{on "click" this.clearFile}}
                >
                  Remove file
                </button>
              {{/unless}}
            </UiCard>
          {{else if @isUploading}}
            <UiCard class="!pt-16 border-dashed border-carbon-40" style="min-height: 200px;">
              <div class="relative mb-2" style="min-height: 50px;">
                <LoadingSpinnerPulse />
              </div>
              <div class="mt-4 -mb-2 text-carbon-50 text-center text-sm">Processing...</div>
            </UiCard>
          {{else}}
            <label class="label-reset cursor-pointer block" for="csv-file-upload" data-test-csv-drop-zone>
              <UiCard class="!pt-16 border-dashed border-carbon-40" style="min-height: 200px;">
                <div class="mx-auto text-center pt-4">
                  <img
                    class="mx-auto mb-8 block"
                    src="/assets/images/document.svg"
                    style="width:50px"
                    alt=""
                    role="none"
                  />
                  <span class="btn btn--small btn--brand cursor-pointer" data-test-csv-select-file>Select CSV file</span>
                </div>
              </UiCard>
            </label>
            <FileInput
              @onLoadFile={{this.onFileSelect}}
              accept=".csv,text/csv"
              class="hidden"
              id="csv-file-upload"
              data-test-csv-file-input
            />
          {{/if}}
        </div>

        <div class="mt-3">
          <MessageBox @type="warning">
            <p class="{{if @higherPrioritySources.length 'mb-2' 'mb-0'}}">
              Uploading a CSV will
              <strong>replace</strong>
              {{if
                @currentMappingCount
                (concat
                  "your current " (concat @currentMappingCount (if (gt @currentMappingCount 1) " mappings" " mapping"))
                )
                "the current mappings"
              }}. Be sure your file includes all employees you intend to map.
            </p>
            {{#if @higherPrioritySources.length}}
              <p class="mb-0">
                Mappings with the following sources will not be overwritten:
                <strong>{{join ", " @higherPrioritySources}}</strong>.
              </p>
            {{/if}}
          </MessageBox>
        </div>

        {{#if this.fileError}}
          <div class="mt-2 text-xs text-red-50" data-test-csv-file-error>
            {{this.fileError}}
          </div>
        {{/if}}

        <div class="mt-4">
          <ul class="list-disc pl-5 text-sm text-carbon-60 space-y-1">
            <li>Your CSV file must list one record per row</li>
            <li>MAC address and employee email are required for each record</li>
            <li>Example CSV file:</li>
          </ul>
          <table class="border border-collapse text-sm mt-2" data-test-csv-example>
            <thead>
              <tr>
                <th class="border p-1 bg-carbon-5">email</th>
                <th class="border p-1 bg-carbon-5">mac_address</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="border p-1">john@example.com</td>
                <td class="border p-1">1a:2b:3c:4d:5e:6f</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="flex justify-end gap-3 pt-4">
          <button type="button" class="btn btn--small btn--outline" data-test-csv-upload-cancel {{on "click" @onClose}}>
            Cancel
          </button>
          <button
            type="button"
            class="btn btn--small btn--brand"
            disabled={{if this.selectedFile false true}}
            data-test-csv-upload-submit
            {{on "click" this.handleUpload}}
          >
            {{if @isUploading "Uploading..." "Upload"}}
          </button>
        </div>
      </div>
    </ModalDialog>
  </template>
}
